<script setup lang="ts">
import type { DirectMessageParticipants } from "../directMessageIdentity";
import { ref, watch, nextTick, onMounted, onBeforeUnmount, type ComponentPublicInstance } from "vue";
import { UiText } from "@shopexperts/shared";
import type { ChatMessage, ParticipantDisplayMap, ProjectSide, RawQuoteRecord } from "@/v2Projects/types";
import ChatMessageBubble from "./ChatMessageBubble.vue";
import { chatDayLabel, isSameChatDay } from "../chatDayLabel";

const props = defineProps<{
  messages: ChatMessage[];
  participantsDisplay: ParticipantDisplayMap | null;
  directParticipants?: DirectMessageParticipants;
  currentUserId: number;
  currentOrgId: number;
  side: ProjectSide;
  isReady: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  focusMessageId?: string | null;
  emptyText?: string;
  openProposalMessageId?: string | null;
  quotes?: RawQuoteRecord[];
  expertRole?: string | null;
  rejectQuote?: (quoteId: number) => Promise<void>;
  viewQuote?: (quoteId: number) => Promise<void>;
}>();

const emit = defineEmits<{
  "load-more": [];
  "read-through": [messageId: string];
  "focus-complete": [payload: { messageId: string; found: boolean }];
}>();

const scrollContainer = ref<HTMLElement | null>(null);
const sentinel = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;
let previousScrollHeight = 0;
let wasAtBottom = true;
let hasDoneInitialScroll = false;
let isPrependingHistory = false;
let prependAnchor: { messageId: string; topOffset: number } | null = null;

const messageElements = new Map<string, HTMLElement>();
const highlightedMessageId = ref<string | null>(null);
const shouldFadeInMessages = ref(false);

let focusRequestVersion = 0;
let highlightTimeoutId: number | null = null;
let isHandlingDeepLinkFocus = Boolean(props.focusMessageId);
let hasCompletedInitialReady = props.isReady;

function isAtBottom(): boolean {
  const el = scrollContainer.value;
  if (!el) return true;
  return el.scrollHeight - el.scrollTop - el.clientHeight < 60;
}

function findQuoteForMessage(message: ChatMessage): RawQuoteRecord | null {
  if (message.kind !== "quote" || typeof message.quote_id !== "number") return null;
  return props.quotes?.find((quote) => quote.id === message.quote_id) ?? null;
}

function scrollToBottom(behavior: ScrollBehavior = "instant") {
  nextTick(() => {
    const el = scrollContainer.value;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior });
  });
}

function preserveScrollPosition() {
  const el = scrollContainer.value;
  if (!el) return;
  const newScrollHeight = el.scrollHeight;
  const diff = newScrollHeight - previousScrollHeight;
  if (diff > 0) {
    el.scrollTop += diff;
  }
}

function setMessageElement(
  id: string,
  element: Element | ComponentPublicInstance | null,
) {
  if (element instanceof HTMLElement) {
    messageElements.set(id, element);
    return;
  }
  messageElements.delete(id);
}

function capturePrependAnchor() {
  const container = scrollContainer.value;
  if (!container) {
    prependAnchor = null;
    return;
  }

  const containerTop = container.getBoundingClientRect().top;

  for (const message of props.messages) {
    const el = messageElements.get(message._id);
    if (!el) continue;

    const rect = el.getBoundingClientRect();
    if (rect.bottom > containerTop + 1) {
      prependAnchor = {
        messageId: message._id,
        topOffset: rect.top - containerTop,
      };
      return;
    }
  }

  prependAnchor = null;
}

function restorePrependAnchor() {
  const container = scrollContainer.value;
  if (!container || !prependAnchor) {
    preserveScrollPosition();
    prependAnchor = null;
    return;
  }

  const anchorEl = messageElements.get(prependAnchor.messageId);
  if (!anchorEl) {
    preserveScrollPosition();
    prependAnchor = null;
    return;
  }

  const containerTop = container.getBoundingClientRect().top;
  const currentOffset = anchorEl.getBoundingClientRect().top - containerTop;
  container.scrollTop += currentOffset - prependAnchor.topOffset;
  prependAnchor = null;
}

function clearHighlight() {
  if (highlightTimeoutId !== null) {
    window.clearTimeout(highlightTimeoutId);
    highlightTimeoutId = null;
  }
  highlightedMessageId.value = null;
}

function highlightMessage(messageId: string) {
  clearHighlight();
  highlightedMessageId.value = messageId;
  highlightTimeoutId = window.setTimeout(() => {
    if (highlightedMessageId.value === messageId) {
      highlightedMessageId.value = null;
    }
    highlightTimeoutId = null;
  }, 4000);
}

async function waitForHistoryLoadToSettle() {
  await nextTick();
  while (props.isLoadingMore) {
    await new Promise((resolve) => window.setTimeout(resolve, 50));
  }
  await nextTick();
}

async function scrollToMessage(messageId: string): Promise<boolean> {
  await nextTick();
  const container = scrollContainer.value;
  const messageElement = messageElements.get(messageId);
  if (!messageElement || !container) {
    return false;
  }

  wasAtBottom = false;
  const centeredTop = Math.max(
    0,
    messageElement.offsetTop - ((container.clientHeight - messageElement.offsetHeight) / 2),
  );
  container.scrollTo({
    top: centeredTop,
    behavior: "smooth",
  });
  highlightMessage(messageId);
  return true;
}

async function focusMessage(messageId: string, version: number) {
  let remainingHistoryLoads = 20;
  isHandlingDeepLinkFocus = true;

  while (version === focusRequestVersion) {
    if (!props.isReady) {
      await new Promise((resolve) => window.setTimeout(resolve, 50));
      continue;
    }

    const wasFound = await scrollToMessage(messageId);
    if (wasFound) {
      isHandlingDeepLinkFocus = false;
      emit("focus-complete", { messageId, found: true });
      return;
    }

    if (!props.hasMore || remainingHistoryLoads <= 0) {
      isHandlingDeepLinkFocus = false;
      emit("focus-complete", { messageId, found: false });
      return;
    }

    if (!props.isLoadingMore) {
      previousScrollHeight = scrollContainer.value?.scrollHeight ?? 0;
      capturePrependAnchor();
      isPrependingHistory = true;
      emit("load-more");
      remainingHistoryLoads -= 1;
    }

    await waitForHistoryLoadToSettle();
  }

  isHandlingDeepLinkFocus = false;
}

function dayDivider(index: number): string | null {
  const msg = props.messages[index];
  if (!msg) return null;
  const prev = props.messages[index - 1];
  return !prev || !isSameChatDay(prev._creationTime, msg._creationTime) ? chatDayLabel(msg._creationTime) : null;
}

function shouldShowSender(index: number): boolean {
  if (index === 0 || dayDivider(index)) return true;
  const msg = props.messages[index];
  const prev = props.messages[index - 1];
  if (!msg || !prev) return true;
  if (msg.kind === "system_notification" || msg.kind === "quote") return true;
  if (prev.kind === "system_notification" || prev.kind === "quote") return true;
  if (msg.sender_snapshot?.user_id !== prev.sender_snapshot?.user_id) return true;
  if (msg.kind !== prev.kind) return true;
  const timeDiff = msg._creationTime - prev._creationTime;
  return timeDiff > 120_000; // 2 minutes
}

function isOwnMessage(msg: ChatMessage): boolean {
  const snap = msg.sender_snapshot;
  if (!snap) return false;
  if (snap.participant_group && props.side !== "admin") {
    return snap.participant_group === props.side;
  }
  return snap.user_id === props.currentUserId || snap.org_id === props.currentOrgId;
}

onMounted(() => {
  scrollToBottom();

  if (sentinel.value) {
    observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (isHandlingDeepLinkFocus) {
          return;
        }
        if (entry?.isIntersecting && props.isReady && props.hasMore && !props.isLoadingMore) {
          previousScrollHeight = scrollContainer.value?.scrollHeight ?? 0;
          capturePrependAnchor();
          isPrependingHistory = true;
          emit("load-more");
        }
      },
      { root: scrollContainer.value, threshold: 0.1 },
    );
    observer.observe(sentinel.value);
  }
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
  clearHighlight();
});

watch(
  () => props.messages.length,
  (newLen, oldLen) => {
    const latestMessage = props.messages[props.messages.length - 1];
    const shouldFollowOwnMessage =
      typeof oldLen === "number" &&
      newLen > oldLen &&
      !!latestMessage &&
      isOwnMessage(latestMessage);

    if (!hasDoneInitialScroll && props.messages.length > 0) {
      hasDoneInitialScroll = true;
      scrollToBottom("instant");
      return;
    }
    if (oldLen === undefined) {
      scrollToBottom();
      return;
    }

    if (isPrependingHistory && newLen > oldLen) {
      nextTick(() => {
        restorePrependAnchor();
        isPrependingHistory = false;
      });
      return;
    }

    if (shouldFollowOwnMessage) {
      scrollToBottom("smooth");
      return;
    }

    if (wasAtBottom && !isHandlingDeepLinkFocus) {
      scrollToBottom("smooth");
    }
  },
  { flush: "post" },
);

watch(
  () => props.messages[0]?._id,
  () => {
    if (props.messages.length === 0) {
      hasDoneInitialScroll = false;
    }
  },
);

watch(
  () => props.isReady,
  (isReady) => {
    if (!isReady || hasCompletedInitialReady) {
      return;
    }

    hasCompletedInitialReady = true;
    shouldFadeInMessages.value = true;
  },
);

watch(
  () => props.isLoadingMore,
  (loading, wasLoading) => {
    if (wasLoading && !loading) {
      isPrependingHistory = false;
      prependAnchor = null;
    }
  },
);

watch(
  () => props.focusMessageId,
  (messageId) => {
    focusRequestVersion += 1;
    if (!messageId) {
      return;
    }
    void focusMessage(messageId, focusRequestVersion);
  },
  { immediate: true },
);

function reportRead() {
  const latest = [...props.messages].reverse().find(message => message.kind === "message" || !!message.project_card);
  if (latest && props.isReady && document.visibilityState === "visible" && isAtBottom()) {
    emit("read-through", latest._id);
  }
}
function handleScroll() {
  wasAtBottom = isAtBottom();
  reportRead();
}
watch(() => props.messages[props.messages.length - 1]?._id, () => { void nextTick(reportRead); });
onMounted(() => {
  document.addEventListener("visibilitychange", reportRead);
});
onBeforeUnmount(() => {
  document.removeEventListener("visibilitychange", reportRead);
});
</script>

<template>
  <div ref="scrollContainer" class="flex-1 overflow-y-auto px-4 py-3 space-y-1" @scroll="handleScroll">
    <!-- Load more sentinel -->
    <div ref="sentinel" class="h-px" />

    <!-- Loading older messages -->
    <div v-if="isReady && isLoadingMore" class="flex justify-center py-3">
      <div class="w-5 h-5 border-2 border-gray-800 border-t-transparent rounded-full animate-spin" />
    </div>

    <!-- Initial messages loading -->
    <div v-if="!isReady" class="flex flex-col items-center justify-center gap-3 py-16" role="status" aria-live="polite">
      <div class="w-5 h-5 border-2 border-gray-800 border-t-transparent rounded-full animate-spin" />
      <UiText as="p" variant="p-small" class="text-gray-500">
        Loading messages...
      </UiText>
    </div>

    <!-- Messages -->
    <template v-else>
      <div class="space-y-1" :class="{ 'chat-feed-initial-fade': shouldFadeInMessages }">
        <div v-for="(msg, idx) in messages" :key="msg._id" :ref="(el) => setMessageElement(msg._id, el)"
          class="transition-all duration-700">
          <div v-if="dayDivider(idx)" class="flex items-center gap-3 pb-2 pt-4" role="separator" :aria-label="dayDivider(idx) ?? undefined">
            <span class="h-px flex-1 bg-se-gray-200" aria-hidden="true" />
            <UiText as="span" variant="small" class="shrink-0 font-medium text-se-gray-500" :text="dayDivider(idx) ?? ''" />
            <span class="h-px flex-1 bg-se-gray-200" aria-hidden="true" />
          </div>
          <ChatMessageBubble :message="msg" :is-own="isOwnMessage(msg)" :side="side"
            :show-sender="shouldShowSender(idx)" :participants-display="participantsDisplay" :direct-participants="directParticipants"
            :quote="findQuoteForMessage(msg)"
            :expert-role="expertRole"
            :is-highlighted="msg._id === highlightedMessageId"
            :auto-open-proposal-details="msg._id === openProposalMessageId"
            :reject-quote="rejectQuote" :view-quote="viewQuote" />
        </div>
      </div>
    </template>

    <!-- Empty state -->
    <div v-if="isReady && messages.length === 0" class="flex flex-col items-center justify-center py-16 gap-2">
      <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
        <svg class="w-5 h-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd"
            d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
            clip-rule="evenodd" />
        </svg>
      </div>
      <UiText as="p" variant="p-small" class="text-gray-500">
        {{ emptyText ?? "No messages yet. Start the conversation." }}
      </UiText>
    </div>
  </div>
</template>

<style scoped>
.chat-feed-initial-fade {
  animation: chat-feed-initial-fade 180ms ease-out;
}

@keyframes chat-feed-initial-fade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}
</style>
